//@flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import CanvasImage from '../components/canvasImage/CanvasImage';
import {getImageUrl} from '../../../../utils/image_provider';
import {imagesHorizontal, imagesVertical} from '../images';
import {IconButton, Hidden, Tabs, Tab, Grid, Snackbar} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {makeStyles} from '@material-ui/core/styles';
import {colors} from '../../../../assets/styles/colors';
import TabPanel from './tabPanel/TabPanel';
import Switch from './switch/Switch';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import type {Store, StoreInformation} from '../../../../entities';
import {usePage} from '../../../page/utils';
import {getCustomDomain} from '../../../../utils/component';

type Props = {|
  store: Store,
  information?: StoreInformation,
|};

type TemplateImage = {
  logo: {
    position: number[],
    maxSize: number[],
  },
  domain: {
    position: number[],
    style: {[keys: string]: any},
    maxLength: number,
    newFontSize: number,
    space: number,
  },
  backgroundImage: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  tabs: {
    borderBottom: `1px solid ${colors.grey300}`,
  },
  customTab: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    '&:hover': {
      color: colors.purple3,
      opacity: 1,
    },
    '&:focus': {
      color: colors.purple3,
    },
  },
  tabPanel: {
    padding: theme.spacing(1),
  },
  snackbar: {
    backgroundColor: '#F5F0FF',
    color: colors.grey700,
  },
  wrapSnackbar: {
    width: '90%',
    margin: '12px',
  },
  alert: {
    backgroundColor: '#F5F0FF',
    color: colors.grey700,
    borderRadius: '12px',
    zIndex: 2,
    position: 'absolute',
    marginTop: '20px',
    [theme.breakpoints.between('md', 1028)]: {
      top: '328px',
      right: theme.spacing(2),
      marginRight: '28px',
      marginLeft: '390px',
    },
    [theme.breakpoints.between(1029, 1669)]: {
      top: '308px',
      right: theme.spacing(2),
      marginRight: '28px',
      marginLeft: '390px',
    },
    [theme.breakpoints.up(1670)]: {
      top: '288px',
      right: theme.spacing(2),
      marginRight: '28px',
      marginLeft: '390px',
    },
    [theme.breakpoints.down('sm')]: {
      top: '300px',
      right: theme.spacing(1),
      left: theme.spacing(1),
    },
  },
  alertIcon: {
    color: `${colors.purple3} !important`,
    display: 'flex',
    alignItems: 'center',
  },
  iconMobile: {
    position: 'absolute',
    marginTop: '-58px',
    right: '32px',
    color: colors.grey600,
    [theme.breakpoints.down('xs')]: {
      marginTop: '-45px',
      right: '28px',
    },
  },
}));

export default usePage<Props>(Templates, 'Templates');

function Templates(props: Props) {
  const {store, information} = props;
  const classes = useStyles();

  const [urlLogo, setUrlLogo] = useState(undefined);
  const [valueTab, setValueTab] = useState(0);
  const [valueSwitch, setValueSwitch] = useState(0);
  const [showAlert, setShowAlert] = useState(true);
  const [showSnackBar, setShowSnackBar] = useState(false);

  const handleChange = (event, newValue) => {
    setValueTab(newValue);
  };
  const handleToogle = (e, newValue) => {
    setValueSwitch(newValue ? newValue : 0);
  };

  useEffect(() => {
    if (information) setUrlLogo(getImageUrl(information.logoId));
  }, [information]);

  const alertSizeLogo = (
    <Alert
      severity="error"
      classes={{
        root: classes.alert,
        icon: classes.alertIcon,
      }}
      onClose={() => setShowAlert(false)}
    >
      Si tu logo no se ve nítido, por favor vuelve a cargarlo. Te recomendamos
      que lo subas en fondo transparente.
      <br />
      <br />
      Para configurar el logo ir a Configuración > Información > Personaliza tu
      tienda
    </Alert>
  );

  const handleClose = () => {
    setShowSnackBar(false);
  };
  const snackBarSizeLogo = (
    <Snackbar
      open={showSnackBar}
      onClose={handleClose}
      // className={classes.wrapSnackbar}
    >
      <Alert
        severity="error"
        className={classes.snackbar}
        classes={{
          icon: classes.alertIcon,
        }}
      >
        Si tu logo no se ve nítido, por favor vuelve a cargarlo. Te recomendamos
        que lo subas en fondo transparente.
        <br />
        <br />
        Para configurar el logo ir a Configuración > Información > Personaliza
        tu tienda
      </Alert>
    </Snackbar>
  );
  return (
    <div className={classes.root}>
      <Hidden mdUp>
        <IconButton
          className={classes.iconMobile}
          component="span"
          onClick={() => setShowSnackBar(!showSnackBar)}
        >
          <ErrorOutlineIcon />
        </IconButton>
      </Hidden>
      <div>
        <Tabs
          value={valueTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          className={classes.tabs}
        >
          <Tab label="Tienda virtual" classes={{root: classes.customTab}} />
        </Tabs>
        <div className={classes.padding} />
      </div>

      <TabPanel value={valueTab} index={0}>
        <Switch
          value={valueSwitch}
          onChange={handleToogle}
          labels={['Formato Cuadrado', 'Formato Vertical']}
        />
        {showAlert && <Hidden smDown>{alertSizeLogo}</Hidden>}

        <Hidden mdUp>{snackBarSizeLogo}</Hidden>
        <TabPanel value={valueSwitch} index={0}>
          <Grid container spacing={4}>
            {imagesHorizontal.map((item: TemplateImage, index: number) => (
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                key={`item-img-h-${index}`}
              >
                <CanvasImage
                  logoImage={urlLogo}
                  logo={item.logo}
                  backgroundImage={item.backgroundImage}
                  domainText={getCustomDomain(store)}
                  domain={item.domain}
                  size={[300, 300]}
                />
              </Grid>
            ))}
          </Grid>
        </TabPanel>
        <TabPanel value={valueSwitch} index={1}>
          <Grid container spacing={2}>
            {imagesVertical.map((item: TemplateImage, index: number) => (
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                key={`item-img-v-${index}`}
              >
                <CanvasImage
                  logoImage={urlLogo}
                  logo={item.logo}
                  backgroundImage={item.backgroundImage}
                  domainText={getCustomDomain(store)}
                  domain={item.domain}
                  size={[300, 533]}
                />
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </TabPanel>
      <TabPanel value={valueTab} index={1}>
        Item two
      </TabPanel>
      <TabPanel value={valueTab} index={2}>
        Item Three
      </TabPanel>
    </div>
  );
}

Templates.propTypes = {
  store: PropTypes.object,
  information: PropTypes.object,
  logo: PropTypes.array,
  domain: PropTypes.array,
  backgroundImage: PropTypes.string,
  size: PropTypes.array,
  style: PropTypes.object,
};
